/**
 * FullInsightsRenderer Component
 *
 * Parses the canonical 5-section `full_insights` text generated by the audit
 * backend and renders it with semantic emphasis (bold scores, colored bullets,
 * priority labels, projected-outcome callout).
 *
 * Falls back to plain `whitespace-pre-line` rendering when the text does not
 * match the expected canonical format.
 */

import type { ReactElement } from 'react';
import { Strong, Subheading, Text } from '@a7y/tailwind-catalyst';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FullInsightsRendererProps {
  text: string;
  className?: string;
}

/** Recognized section header keys in the canonical format. */
type SectionKey =
  | 'overall'
  | 'whats_working'
  | 'whats_improvement'
  | 'priority_actions'
  | 'projected_outcome'
  | 'custom';

interface ParsedSection {
  key: SectionKey;
  /** Raw header line (e.g. "What's working:") */
  header: string;
  /** Body lines (may include "- " prefixed bullets). */
  lines: string[];
}

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

const SECTION_PATTERNS: Array<{ key: SectionKey; pattern: RegExp }> = [
  { key: 'overall', pattern: /^Overall:/i },
  { key: 'whats_working', pattern: /^(What(?:'s|\s+is)?\s+working|Strengths):/i },
  {
    key: 'whats_improvement',
    pattern: /^(What(?:'s|\s+is)?\s+needs?\s+improvement|Areas\s+for\s+Improvement):/i,
  },
  { key: 'priority_actions', pattern: /^Priority actions:/i },
  { key: 'projected_outcome', pattern: /^Projected outcome:/i },
];

function detectSectionKey(line: string): SectionKey | null {
  for (const { key, pattern } of SECTION_PATTERNS) {
    if (pattern.test(line.trim())) return key;
  }
  return null;
}

/**
 * Returns true if the text looks like the canonical 5-section format.
 * We require at least the "Overall:" header to be present.
 */
function isCanonicalFormat(text: string): boolean {
  return /^Overall:/im.test(text);
}

function parseSections(text: string): ParsedSection[] {
  const blocks = text.split(/\n\n+/);
  const sections: ParsedSection[] = [];

  for (const block of blocks) {
    const trimmedBlock = block.trim();
    if (!trimmedBlock) continue;

    const lines = trimmedBlock.split('\n');
    const firstLine = lines[0].trim();

    // "Overall:" is special -- it's a single-line section whose content IS the header line.
    if (/^Overall:/i.test(firstLine)) {
      sections.push({
        key: 'overall',
        header: firstLine,
        lines: lines.slice(1).map((l) => l.trim()),
      });
      continue;
    }

    // "Projected outcome:" is also typically a single-line section.
    if (/^Projected outcome:/i.test(firstLine)) {
      sections.push({
        key: 'projected_outcome',
        header: firstLine,
        lines: lines.slice(1).map((l) => l.trim()),
      });
      continue;
    }

    const key = detectSectionKey(firstLine);
    if (key) {
      sections.push({
        key,
        header: firstLine,
        lines: lines.slice(1).map((l) => l.trim()),
      });
      continue;
    }

    const customHeader = firstLine.match(/^—\s*(.*?)\s*—$/)?.[1]?.trim() ?? firstLine;
    sections.push({
      key: 'custom',
      header: customHeader,
      lines: lines.slice(1).map((l) => l.trim()),
    });
  }

  return sections;
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

/** Render the "Overall:" line with the score in bold. */
function OverallLine({ header }: { header: string }): ReactElement {
  // Try to extract the score pattern (e.g. "72/100")
  const normalizedHeader = header.replace(/^Overall:\s*/i, '').trim();
  const scoreMatch = header.match(/(\d+\/\d+)/);
  if (!scoreMatch) {
    return (
      <section>
        <Subheading level={3}>Overall</Subheading>
        <Text className="mt-1">{normalizedHeader || header}</Text>
      </section>
    );
  }

  const scoreText = scoreMatch[1];
  const idx = normalizedHeader.indexOf(scoreText);
  const before = normalizedHeader.slice(0, idx);
  const after = normalizedHeader.slice(idx + scoreText.length);

  return (
    <section>
      <Subheading level={3}>Overall</Subheading>
      <Text className="mt-1">
        {before}
        <Strong className="font-bold">{scoreText}</Strong>
        {after}
      </Text>
    </section>
  );
}

/** Render a list section with colored bullet indicators. */
function BulletSection({
  header,
  lines,
  bulletColor,
}: {
  header: string;
  lines: string[];
  bulletColor: string;
}): ReactElement {
  return (
    <div className="mt-4">
      <Subheading level={3}>{header}</Subheading>
      {lines.length > 0 && (
        <ul className="mt-1 space-y-1">
          {lines.map((line, i) => {
            const text = line.replace(/^-\s*/, '');
            return (
              <li
                key={i}
                className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
              >
                <span className={`${bulletColor} mt-0.5`}>&#8226;</span>
                <span>{text}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Priority color by priority number. */
function getPriorityColor(priority: number): string {
  switch (priority) {
    case 1:
      return 'text-red-600 dark:text-red-400';
    case 2:
      return 'text-amber-600 dark:text-amber-400';
    case 3:
      return 'text-blue-600 dark:text-blue-400';
    default:
      return 'text-zinc-600 dark:text-zinc-400';
  }
}

/** Render the "Priority actions:" section with numbered priority labels. */
function PriorityActionsSection({
  header,
  lines,
}: {
  header: string;
  lines: string[];
}): ReactElement {
  return (
    <div className="mt-4">
      <Subheading level={3}>{header}</Subheading>
      {lines.length > 0 && (
        <ul className="mt-1 space-y-1">
          {lines.map((line, i) => {
            const text = line.replace(/^-\s*/, '');
            // Match "Priority N" prefix pattern: "Priority 1 -- action: effect."
            const priorityMatch = text.match(/^(Priority\s+(\d+))\s*--\s*(.*)/i);
            if (priorityMatch) {
              const priorityLabel = priorityMatch[1];
              const priorityNum = parseInt(priorityMatch[2], 10);
              const rest = priorityMatch[3];
              const color = getPriorityColor(priorityNum);
              return (
                <li
                  key={i}
                  className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
                >
                  <span className="text-amber-500 mt-0.5">&#8226;</span>
                  <span>
                    <span className={`font-semibold ${color}`}>{priorityLabel}</span>
                    {' -- '}
                    {rest}
                  </span>
                </li>
              );
            }
            // Fallback: render as regular amber bullet
            return (
              <li
                key={i}
                className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
              >
                <span className="text-amber-500 mt-0.5">&#8226;</span>
                <span>{text}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Render the "Projected outcome:" section as a callout box. */
function ProjectedOutcomeCallout({
  header,
  lines,
}: {
  header: string;
  lines: string[];
}): ReactElement {
  // The content may be on the header line itself (after "Projected outcome:")
  // or on subsequent lines.
  const headerContent = header.replace(/^Projected outcome:\s*/i, '').trim();
  const allContent = [headerContent, ...lines].filter(Boolean).join('\n');

  return (
    <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-lg px-3 py-2">
      <Text className="font-medium text-blue-900 dark:text-blue-200">
        <Strong className="font-semibold text-blue-900 dark:text-blue-200">
          Projected outcome:
        </Strong>{' '}
        {allContent}
      </Text>
    </div>
  );
}

function CustomSection({ header, lines }: { header: string; lines: string[] }): ReactElement {
  const body = lines.filter(Boolean).join('\n');
  return (
    <div className="mt-4">
      <Subheading level={3}>{header}</Subheading>
      {body ? <Text className="mt-1 whitespace-pre-line">{body}</Text> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FullInsightsRenderer({
  text,
  className,
}: FullInsightsRendererProps): ReactElement | null {
  if (!text || !text.trim()) {
    return null;
  }

  // Fallback: if text doesn't match canonical format, render as plain text
  if (!isCanonicalFormat(text)) {
    return (
      <Text className={`whitespace-pre-line text-zinc-700 dark:text-zinc-200 ${className ?? ''}`}>
        {text}
      </Text>
    );
  }

  const sections = parseSections(text);

  return (
    <div className={className}>
      {sections.map((section, i) => {
        switch (section.key) {
          case 'overall':
            return <OverallLine key={i} header={section.header} />;

          case 'whats_working':
            return (
              <BulletSection
                key={i}
                header={section.header}
                lines={section.lines}
                bulletColor="text-green-500"
              />
            );

          case 'whats_improvement':
            return (
              <BulletSection
                key={i}
                header={section.header}
                lines={section.lines}
                bulletColor="text-amber-500"
              />
            );

          case 'priority_actions':
            return <PriorityActionsSection key={i} header={section.header} lines={section.lines} />;

          case 'projected_outcome':
            return (
              <ProjectedOutcomeCallout key={i} header={section.header} lines={section.lines} />
            );

          case 'custom':
            return <CustomSection key={i} header={section.header} lines={section.lines} />;

          default:
            return null;
        }
      })}
    </div>
  );
}
